import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, XCircle } from 'lucide-react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  activateOneTimePass,
  activatePlan,
  fetchBenefits,
  fetchPlans,
  grantBenefit,
  revertPlan,
  endOneTimePass,
  fetchOneTimePass,
  revokeBenefit,
  ENTITLEMENT_PLATFORMS,
  type EntitlementPlatform,
  type OrganizationBenefit,
  type Plan,
} from '@/lib/adminCredits';
import { useMe } from '@/lib/useMe';

const PLANS_QUERY_KEY = ['admin-plans'] as const;
const BENEFITS_QUERY_KEY = (orgId: string) => ['admin-benefits', orgId] as const;
const ONE_TIME_PASS_QUERY_KEY = (orgId: string) => ['admin-one-time-pass', orgId] as const;
const MIN_REASON_LENGTH = 10;

/** Mirrors app/models/entitlements.FEATURE_KEYS — the only benefit_key values
 * the API accepts (a benefit is a feature grant with no subscription behind
 * it). Kept in one place here rather than re-derived from a plan's feature
 * map, since a plan need not carry every feature this product has. */
const FEATURE_KEYS = ['performance', 'content_generation'];

type Notice = { ok: true; message: string } | { ok: false; message: string };

/**
 * Which platform this activation is FOR (ClickUp 869f084w5 §3). The note under
 * the select is not decoration: it is the only place a support agent is told
 * that choosing APP labels OUR record and does not route anything to an App
 * client, because there is no App client yet. Leaving it out is how "manage
 * App and Web separately" gets read as a capability that exists.
 */
function PlatformField({
  id,
  value,
  onChange,
}: {
  id: string;
  value: EntitlementPlatform;
  onChange: (next: EntitlementPlatform) => void;
}) {
  return (
    <label className="flex flex-col gap-1 text-meta font-medium text-fg-secondary" htmlFor={id}>
      Platform
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value as EntitlementPlatform)}
        className="rounded-card border border-border bg-background px-3 py-2 text-body text-fg-primary"
      >
        {ENTITLEMENT_PLATFORMS.map((platform) => (
          <option key={platform} value={platform}>
            {platform === 'WEB' ? 'Web' : 'App'}
          </option>
        ))}
      </select>
      <span className="text-micro font-normal text-fg-tertiary">
        Stamped on the entitlement row and its audit receipt. There is no App client yet — every
        payment and every account on this platform is Web today — so App labels the record for
        later, it does not activate anything in an app.
      </span>
    </label>
  );
}

function Banner({ notice }: { notice: Notice | null }) {
  if (!notice) return null;
  return (
    <div
      role="status"
      className={`mt-4 flex items-center gap-2 rounded-card border px-3 py-2 text-body ${
        notice.ok
          ? 'border-success-border bg-success-soft text-success-soft-foreground'
          : 'border-destructive-border bg-destructive-soft text-destructive-soft-foreground'
      }`}
    >
      {notice.ok ? (
        <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
      ) : (
        <XCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
      )}
      {notice.message}
    </div>
  );
}

export function PlanPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const canManage = me?.role === 'finance' || me?.role === 'superadmin';

  const [planId, setPlanId] = useState('');
  const [planValidUntil, setPlanValidUntil] = useState('');
  const [planReason, setPlanReason] = useState('');
  const [planPlatform, setPlanPlatform] = useState<EntitlementPlatform>('WEB');
  const [confirmActivate, setConfirmActivate] = useState(false);
  const [confirmRevert, setConfirmRevert] = useState(false);
  const [planNotice, setPlanNotice] = useState<Notice | null>(null);

  const [benefitKey, setBenefitKey] = useState(FEATURE_KEYS[0]);
  const [benefitValidUntil, setBenefitValidUntil] = useState('');
  const [benefitReason, setBenefitReason] = useState('');
  const [benefitPlatform, setBenefitPlatform] = useState<EntitlementPlatform>('WEB');

  const [passMultiplier, setPassMultiplier] = useState('2');
  const [passDays, setPassDays] = useState('30');
  const [passReason, setPassReason] = useState('');
  const [passPlatform, setPassPlatform] = useState<EntitlementPlatform>('WEB');
  const [confirmPass, setConfirmPass] = useState(false);
  const [confirmEndPass, setConfirmEndPass] = useState(false);
  const [passNotice, setPassNotice] = useState<Notice | null>(null);
  // A money-shaped endpoint takes an Idempotency-Key, and a NEW key is minted
  // after each success — reusing the previous one would make a genuinely new
  // pass replay as the old one (same rule as CreditsPage's grant key).
  const [passKey, setPassKey] = useState(() => crypto.randomUUID());
  const [confirmBenefit, setConfirmBenefit] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<OrganizationBenefit | null>(null);
  const [benefitNotice, setBenefitNotice] = useState<Notice | null>(null);

  const { data: plans } = useQuery({ queryKey: PLANS_QUERY_KEY, queryFn: fetchPlans });
  const { data: benefits } = useQuery({
    queryKey: BENEFITS_QUERY_KEY(orgId ?? ''),
    queryFn: () => fetchBenefits(orgId!),
    enabled: !!orgId,
  });
  const { data: oneTimePass } = useQuery({
    queryKey: ONE_TIME_PASS_QUERY_KEY(orgId ?? ''),
    queryFn: () => fetchOneTimePass(orgId!),
    enabled: !!orgId,
  });

  const selectedPlan: Plan | undefined = plans?.find((p) => p.id === planId);
  const planValidUntilRequired = !!planId && !selectedPlan?.is_default;
  const planFormValid =
    !!planId &&
    planReason.trim().length >= MIN_REASON_LENGTH &&
    (!planValidUntilRequired || !!planValidUntil);

  const activateMutation = useMutation({
    mutationFn: () =>
      activatePlan(orgId!, {
        plan_id: planId,
        valid_until: planValidUntil ? new Date(planValidUntil).toISOString() : null,
        reason: planReason.trim(),
        platform: planPlatform,
      }),
    onSuccess: (res) => {
      setPlanNotice({
        ok: true,
        message: `Activated for ${res.platform ?? planPlatform} — audit/benefit row ${
          res.benefit_id ?? '(free plan, no expiry row)'
        }`,
      });
      setPlanId('');
      setPlanValidUntil('');
      setPlanReason('');
      void queryClient.invalidateQueries({ queryKey: BENEFITS_QUERY_KEY(orgId ?? '') });
    },
    onError: (err: Error) => setPlanNotice({ ok: false, message: err.message }),
  });

  const revertMutation = useMutation({
    mutationFn: () => revertPlan(orgId!, 'manual revert from the admin console'),
    onSuccess: (res) => {
      setPlanNotice({ ok: true, message: `Reverted to plan ${res.plan_id ?? '(none)'}` });
      void queryClient.invalidateQueries({ queryKey: BENEFITS_QUERY_KEY(orgId ?? '') });
    },
    onError: (err: Error) => setPlanNotice({ ok: false, message: err.message }),
  });

  const benefitFormValid =
    !!benefitKey && !!benefitValidUntil && benefitReason.trim().length >= MIN_REASON_LENGTH;

  const grantBenefitMutation = useMutation({
    mutationFn: () =>
      grantBenefit(orgId!, {
        benefit_key: benefitKey,
        valid_until: new Date(benefitValidUntil).toISOString(),
        reason: benefitReason.trim(),
        platform: benefitPlatform,
      }),
    onSuccess: (res) => {
      setBenefitNotice({
        ok: true,
        message: `Granted for ${res.platform ?? benefitPlatform} — audit/benefit row ${res.benefit_id}`,
      });
      setBenefitValidUntil('');
      setBenefitReason('');
      void queryClient.invalidateQueries({ queryKey: BENEFITS_QUERY_KEY(orgId ?? '') });
    },
    onError: (err: Error) => setBenefitNotice({ ok: false, message: err.message }),
  });

  const revokeMutation = useMutation({
    mutationFn: (benefit: OrganizationBenefit) =>
      revokeBenefit(orgId!, benefit.id, 'manual revoke from the admin console'),
    onSuccess: (res) => {
      setBenefitNotice({ ok: true, message: `Revoked ${res.key} — restored to ${String(res.reverted_to)}` });
      void queryClient.invalidateQueries({ queryKey: BENEFITS_QUERY_KEY(orgId ?? '') });
    },
    onError: (err: Error) => setBenefitNotice({ ok: false, message: err.message }),
  });

  const passMultiplierNumber = Number(passMultiplier);
  const passDaysNumber = Number(passDays);
  const passFormValid =
    Number.isFinite(passMultiplierNumber) &&
    passMultiplierNumber > 1 &&
    Number.isInteger(passDaysNumber) &&
    passDaysNumber > 0 &&
    passReason.trim().length >= MIN_REASON_LENGTH;

  const activatePassMutation = useMutation({
    mutationFn: () =>
      activateOneTimePass(
        orgId!,
        {
          multiplier: passMultiplierNumber,
          days: passDaysNumber,
          reason: passReason.trim(),
          platform: passPlatform,
        },
        passKey
      ),
    onSuccess: (res) => {
      setPassNotice({
        ok: true,
        message: res.replayed
          ? `Already active (replayed) — ${res.multiplier}x for ${res.platform ?? passPlatform} until ${res.active_until ?? 'unknown'}`
          : `Activated — ${res.multiplier}x for ${res.platform ?? passPlatform} until ${res.active_until ?? 'unknown'}`,
      });
      setPassReason('');
      setPassKey(crypto.randomUUID());
      void queryClient.invalidateQueries({ queryKey: ONE_TIME_PASS_QUERY_KEY(orgId ?? '') });
    },
    onError: (err: Error) => setPassNotice({ ok: false, message: err.message }),
  });

  const endPassMutation = useMutation({
    mutationFn: () => endOneTimePass(orgId!, 'manual end from the admin console'),
    onSuccess: () => {
      setPassNotice({ ok: true, message: 'The one-time pass was ended.' });
      void queryClient.invalidateQueries({ queryKey: ONE_TIME_PASS_QUERY_KEY(orgId ?? '') });
    },
    onError: (err: Error) => setPassNotice({ ok: false, message: err.message }),
  });

  if (!orgId) return null;

  const activePlanBenefit = benefits?.find((b) => b.key === 'plan' && !b.revoked_at);
  const activeStandaloneBenefits = (benefits ?? []).filter(
    (b) => b.key !== 'plan' && !b.revoked_at
  );

  return (
    <div className="max-w-2xl">
      <h1 className="text-h2 font-semibold text-fg-primary">Plan &amp; benefits — {orgId}</h1>

      {!canManage && (
        <p className="mt-2 text-body text-fg-secondary">
          Your role ({me?.role}) can view this page but cannot activate or revert — finance or
          superadmin only.
        </p>
      )}

      <section className="mt-6 rounded-card border border-border bg-surface-field p-4">
        <h2 className="text-h3 font-semibold text-fg-primary">Manual plan activation</h2>
        {activePlanBenefit && (
          <p className="mt-1 text-meta text-fg-secondary">
            Active manual activation: plan {String(activePlanBenefit.value.plan_id)} for{' '}
            {activePlanBenefit.platform ?? 'WEB'}, valid until{' '}
            {activePlanBenefit.valid_until ?? 'no expiry'}.
          </p>
        )}
        {canManage && (
          <form
            className="mt-3 flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (planFormValid) setConfirmActivate(true);
            }}
          >
            <label className="flex flex-col gap-1 text-meta font-medium text-fg-secondary">
              Plan
              <select
                value={planId}
                onChange={(e) => setPlanId(e.target.value)}
                className="rounded-card border border-border bg-background px-3 py-2 text-body text-fg-primary"
              >
                <option value="">Select a plan…</option>
                {(plans ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {p.is_default ? ' (Free)' : ''}
                  </option>
                ))}
              </select>
            </label>
            <PlatformField
              id="plan-platform"
              value={planPlatform}
              onChange={setPlanPlatform}
            />
            <label className="flex flex-col gap-1 text-meta font-medium text-fg-secondary">
              Valid until{planValidUntilRequired ? ' (required)' : ' (not required for Free)'}
              <input
                type="datetime-local"
                value={planValidUntil}
                onChange={(e) => setPlanValidUntil(e.target.value)}
                className="rounded-card border border-border bg-background px-3 py-2 text-body text-fg-primary"
              />
            </label>
            <label className="flex flex-col gap-1 text-meta font-medium text-fg-secondary">
              Reason (required, at least {MIN_REASON_LENGTH} characters)
              <textarea
                value={planReason}
                onChange={(e) => setPlanReason(e.target.value)}
                rows={2}
                className="rounded-card border border-border bg-background px-3 py-2 text-body text-fg-primary"
              />
            </label>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={!planFormValid || activateMutation.isPending}
                className="rounded-card bg-primary px-4 py-2 text-body font-medium text-primary-foreground hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                Activate plan
              </button>
              {activePlanBenefit && (
                <button
                  type="button"
                  onClick={() => setConfirmRevert(true)}
                  disabled={revertMutation.isPending}
                  className="rounded-card border border-border px-4 py-2 text-body font-medium text-fg-primary hover:bg-state-hover disabled:opacity-50"
                >
                  Revert to previous plan
                </button>
              )}
            </div>
          </form>
        )}
        <Banner notice={planNotice} />
      </section>

      <section className="mt-6 rounded-card border border-border bg-surface-field p-4">
        <h2 className="text-h3 font-semibold text-fg-primary">One-time pass (Booster)</h2>
        <p className="mt-1 text-meta text-fg-secondary">
          A one-time pass multiplies what this organization BUYS while the window is open — the
          same mechanism a paid Booster order opens. It does not grant credits by itself; use
          Credits for that.
        </p>
        {oneTimePass?.multiplier ? (
          <p className="mt-2 text-meta text-fg-primary">
            Active: {oneTimePass.multiplier}x until {oneTimePass.active_until ?? 'unknown'}
            {oneTimePass.source ? ` (source: ${oneTimePass.source})` : ''}
          </p>
        ) : (
          <p className="mt-2 text-meta text-fg-tertiary">No one-time pass is active.</p>
        )}
        {canManage && (
          <form
            className="mt-3 flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (passFormValid) setConfirmPass(true);
            }}
          >
            <label
              className="flex flex-col gap-1 text-meta font-medium text-fg-secondary"
              htmlFor="pass-multiplier"
            >
              Multiplier (greater than 1)
              <input
                id="pass-multiplier"
                type="number"
                min={1.1}
                step={0.1}
                value={passMultiplier}
                onChange={(e) => setPassMultiplier(e.target.value)}
                className="rounded-card border border-border bg-background px-3 py-2 text-body text-fg-primary"
              />
            </label>
            <label
              className="flex flex-col gap-1 text-meta font-medium text-fg-secondary"
              htmlFor="pass-days"
            >
              Validity period, in days (required)
              <input
                id="pass-days"
                type="number"
                min={1}
                step={1}
                value={passDays}
                onChange={(e) => setPassDays(e.target.value)}
                className="rounded-card border border-border bg-background px-3 py-2 text-body text-fg-primary"
              />
            </label>
            <PlatformField id="pass-platform" value={passPlatform} onChange={setPassPlatform} />
            <label className="flex flex-col gap-1 text-meta font-medium text-fg-secondary">
              Reason (required, at least {MIN_REASON_LENGTH} characters)
              <textarea
                value={passReason}
                onChange={(e) => setPassReason(e.target.value)}
                rows={2}
                className="rounded-card border border-border bg-background px-3 py-2 text-body text-fg-primary"
              />
            </label>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={!passFormValid || activatePassMutation.isPending}
                className="rounded-card bg-primary px-4 py-2 text-body font-medium text-primary-foreground hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                Activate one-time pass
              </button>
              {oneTimePass?.multiplier ? (
                <button
                  type="button"
                  onClick={() => setConfirmEndPass(true)}
                  disabled={endPassMutation.isPending}
                  className="rounded-card border border-border px-4 py-2 text-body font-medium text-fg-primary hover:bg-state-hover disabled:opacity-50"
                >
                  End it now
                </button>
              ) : null}
            </div>
          </form>
        )}
        <Banner notice={passNotice} />
      </section>

      <section className="mt-6 rounded-card border border-border bg-surface-field p-4">
        <h2 className="text-h3 font-semibold text-fg-primary">Standalone benefits</h2>
        {canManage && (
          <form
            className="mt-3 flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (benefitFormValid) setConfirmBenefit(true);
            }}
          >
            <label className="flex flex-col gap-1 text-meta font-medium text-fg-secondary">
              Benefit
              <select
                value={benefitKey}
                onChange={(e) => setBenefitKey(e.target.value)}
                className="rounded-card border border-border bg-background px-3 py-2 text-body text-fg-primary"
              >
                {FEATURE_KEYS.map((key) => (
                  <option key={key} value={key}>
                    {key}
                  </option>
                ))}
              </select>
            </label>
            <PlatformField
              id="benefit-platform"
              value={benefitPlatform}
              onChange={setBenefitPlatform}
            />
            <label className="flex flex-col gap-1 text-meta font-medium text-fg-secondary">
              Valid until (required)
              <input
                type="datetime-local"
                value={benefitValidUntil}
                onChange={(e) => setBenefitValidUntil(e.target.value)}
                className="rounded-card border border-border bg-background px-3 py-2 text-body text-fg-primary"
              />
            </label>
            <label className="flex flex-col gap-1 text-meta font-medium text-fg-secondary">
              Reason (required, at least {MIN_REASON_LENGTH} characters)
              <textarea
                value={benefitReason}
                onChange={(e) => setBenefitReason(e.target.value)}
                rows={2}
                className="rounded-card border border-border bg-background px-3 py-2 text-body text-fg-primary"
              />
            </label>
            <button
              type="submit"
              disabled={!benefitFormValid || grantBenefitMutation.isPending}
              className="self-start rounded-card bg-primary px-4 py-2 text-body font-medium text-primary-foreground hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              Grant benefit
            </button>
          </form>
        )}
        <Banner notice={benefitNotice} />

        <ul className="mt-4 flex flex-col gap-2">
          {activeStandaloneBenefits.map((b) => (
            <li
              key={b.id}
              className="flex items-center justify-between rounded-card border border-border px-3 py-2 text-body"
            >
              <span className="text-fg-primary">
                {b.key} — {b.platform ?? 'WEB'} — valid until {b.valid_until ?? 'no expiry'}
              </span>
              {canManage && (
                <button
                  type="button"
                  onClick={() => setRevokeTarget(b)}
                  className="text-meta font-medium text-destructive hover:underline"
                >
                  Revoke
                </button>
              )}
            </li>
          ))}
          {activeStandaloneBenefits.length === 0 && (
            <li className="text-meta text-fg-tertiary">No active standalone benefits.</li>
          )}
        </ul>
      </section>

      {confirmPass && (
        <ConfirmDialog
          title="Activate a one-time pass?"
          description={`Multiply everything ${orgId} buys by ${passMultiplier} for ${passDays} days, recorded for ${passPlatform}. Reason: "${passReason.trim()}".`}
          confirmLabel="Activate"
          busy={activatePassMutation.isPending}
          onCancel={() => setConfirmPass(false)}
          onConfirm={() => {
            activatePassMutation.mutate();
            setConfirmPass(false);
          }}
        />
      )}
      {confirmEndPass && (
        <ConfirmDialog
          title="End the one-time pass now?"
          description={`The window closes immediately for ${orgId}. The row is kept and marked ended, not deleted.`}
          confirmLabel="End it"
          busy={endPassMutation.isPending}
          onCancel={() => setConfirmEndPass(false)}
          onConfirm={() => {
            endPassMutation.mutate();
            setConfirmEndPass(false);
          }}
        />
      )}
      {confirmActivate && (
        <ConfirmDialog
          title="Activate plan?"
          description={`Set ${orgId}'s plan to ${selectedPlan?.name ?? planId} for ${planPlatform}${
            planValidUntil ? ` until ${planValidUntil}` : ''
          }. Reason: "${planReason.trim()}".`}
          confirmLabel="Activate"
          busy={activateMutation.isPending}
          onCancel={() => setConfirmActivate(false)}
          onConfirm={() => {
            activateMutation.mutate();
            setConfirmActivate(false);
          }}
        />
      )}
      {confirmRevert && (
        <ConfirmDialog
          title="Revert plan?"
          description={`Restore ${orgId}'s previous plan, undoing the current manual activation.`}
          confirmLabel="Revert"
          busy={revertMutation.isPending}
          onCancel={() => setConfirmRevert(false)}
          onConfirm={() => {
            revertMutation.mutate();
            setConfirmRevert(false);
          }}
        />
      )}
      {confirmBenefit && (
        <ConfirmDialog
          title="Grant benefit?"
          description={`Grant "${benefitKey}" to ${orgId} until ${benefitValidUntil}. Reason: "${benefitReason.trim()}".`}
          confirmLabel="Grant"
          busy={grantBenefitMutation.isPending}
          onCancel={() => setConfirmBenefit(false)}
          onConfirm={() => {
            grantBenefitMutation.mutate();
            setConfirmBenefit(false);
          }}
        />
      )}
      {revokeTarget && (
        <ConfirmDialog
          title="Revoke benefit?"
          description={`Revoke "${revokeTarget.key}" from ${orgId} — restores the previous value.`}
          confirmLabel="Revoke"
          busy={revokeMutation.isPending}
          onCancel={() => setRevokeTarget(null)}
          onConfirm={() => {
            revokeMutation.mutate(revokeTarget);
            setRevokeTarget(null);
          }}
        />
      )}
    </div>
  );
}
